import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import type { IDateAdapter } from '@/modern/core'

export interface PwaInstallEvents {
    onInstallPromptShown?: () => void
    onInstallAccepted?: () => void
    onInstallDeclined?: () => void
    onAppInstalled?: () => void
}

const ASK_INSTALL_PWA_NEXT_DAY = 'ask-install-pwa'

export function usePwaInstall(
    dateAdapter: IDateAdapter,
    events?: PwaInstallEvents
) {
    let installPrompt: any | null = null

    const isInstallModalOpen = ref(false)

    /**
     * Data (string) armazenada no localStorage
     * Ex: '2026-01-22'
     */
    const nextAskDateRaw = useLocalStorage<string | null>(
        ASK_INSTALL_PWA_NEXT_DAY,
        null
    )

    /**
     * Data convertida para Date
     */
    const nextAskDate = computed<Date | null>(() => {
        return nextAskDateRaw.value
            ? dateAdapter.toJSDate(nextAskDateRaw.value)
            : null
    })

    function setNextAskInDays(days: number) {
        nextAskDateRaw.value = dateAdapter.addDays(
            dateAdapter.now(),
            days
        )
    }

    function canShowInstallPrompt() {
        if (import.meta.env.VITE_ENV !== 'production') return false
        if (!nextAskDate.value) return true
        return nextAskDate.value <= dateAdapter.now()
    }

    function onBeforeInstallPrompt(event: Event) {
        if (!canShowInstallPrompt()) return

        event.preventDefault()
        installPrompt = event
        isInstallModalOpen.value = true
        events?.onInstallPromptShown?.()
    }

    function acceptInstall() {
        if (!installPrompt) return

        installPrompt.prompt()

        installPrompt.userChoice.then((choiceResult: any) => {
            if (choiceResult.outcome === 'accepted') {
                events?.onInstallAccepted?.()
            } else {
                setNextAskInDays(1)
                events?.onInstallDeclined?.()
            }

            closeModal()
        })
    }

    function declineInstall() {
        setNextAskInDays(1)
        events?.onInstallDeclined?.()
        closeModal()
    }

    function closeModal() {
        nextTick(() => {
            isInstallModalOpen.value = false
            installPrompt = null
        })
    }

    function onAppInstalled() {
        events?.onAppInstalled?.()
    }

    onMounted(() => {
        window.addEventListener(
            'beforeinstallprompt',
            onBeforeInstallPrompt as EventListener
        )
        window.addEventListener('appinstalled', onAppInstalled)
    })

    onUnmounted(() => {
        window.removeEventListener(
            'beforeinstallprompt',
            onBeforeInstallPrompt as EventListener
        )
        window.removeEventListener('appinstalled', onAppInstalled)
    })

    return {
        isInstallModalOpen,
        acceptInstall,
        declineInstall
    }
}